import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { MaintenancePeriod, SystemConfig } from '@common/models/config/system.modl'
import {
  getActiveMaintenancePeriod,
  getSystemConfigVersion,
  isRemoteVersionNewer,
  isUnderMaintenance,
} from '@common/models/config/system.modl'
import { SystemConfigService } from '@common/services/system-config-service'
import { VersionReloadDialog } from '@common/components/VersionReloadDialog'
import MaintenanceScreen from '@/views/error/maintenance'
import packageJson from '../../package.json'

type SystemConfigContextType = {
  systemConfig: SystemConfig | null
  now: Date
  isMaintenance: boolean
  activeMaintenancePeriod: MaintenancePeriod | null
  lastMaintenancePeriod: MaintenancePeriod | null
  isLoading: boolean
  error: Error | null
}

const CURRENT_APP_VERSION = packageJson.version ?? '0.0.0'
const MAINTENANCE_CHECK_INTERVAL_MS = 60_000

const SystemConfigContext = createContext<SystemConfigContextType>({
  systemConfig: null,
  now: new Date(),
  isMaintenance: false,
  activeMaintenancePeriod: null,
  lastMaintenancePeriod: null,
  isLoading: true,
  error: null,
})

export const SystemConfigProvider = ({ children }: { children: ReactNode }) => {
  const [systemConfig, setSystemConfig] = useState<SystemConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), MAINTENANCE_CHECK_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const service = new SystemConfigService()
    const unsubscribe = service.subscribeSystemConfig(
      (config) => {
        setSystemConfig(config)
        setError(null)
        setIsLoading(false)
      },
      (err) => {
        console.error('SystemConfig subscription error:', err)
        setError(err)
        setIsLoading(false)
      },
    )
    return () => unsubscribe()
  }, [])

  const isMaintenance = useMemo(
    () => (systemConfig ? isUnderMaintenance(systemConfig, now) : false),
    [systemConfig, now]
  )
  const activeMaintenancePeriod = useMemo(
    () => (systemConfig ? getActiveMaintenancePeriod(systemConfig, now) : null),
    [systemConfig, now]
  )
  const lastMaintenancePeriod = useMemo(() => {
    if (!systemConfig?.maintenance?.length) return null
    return systemConfig.maintenance[systemConfig.maintenance.length - 1] ?? null
  }, [systemConfig])
  const latestAppVersion = useMemo(
    () => (systemConfig ? getSystemConfigVersion(systemConfig, 'town') : null),
    [systemConfig]
  )
  const shouldShowVersionReloadDialog = useMemo(
    () => !!(latestAppVersion && isRemoteVersionNewer(CURRENT_APP_VERSION, latestAppVersion)),
    [latestAppVersion]
  )

  const value = useMemo(
    () => ({
      systemConfig,
      now,
      isMaintenance,
      activeMaintenancePeriod,
      lastMaintenancePeriod,
      isLoading,
      error,
    }),
    [systemConfig, now, isMaintenance, activeMaintenancePeriod, lastMaintenancePeriod, isLoading, error]
  )

  const maintenancePeriod = lastMaintenancePeriod ?? activeMaintenancePeriod
  const renderBody = () => {
    if (isLoading) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '18px',
            color: '#666',
          }}
        >
          読み込み中...
        </div>
      )
    }
    if (error) {
      return <MaintenanceScreen activeMaintenancePeriod={null} />
    }
    if (isMaintenance) {
      return <MaintenanceScreen activeMaintenancePeriod={maintenancePeriod} />
    }
    return children
  }

  return (
    <SystemConfigContext.Provider value={value}>
      {renderBody()}
      {shouldShowVersionReloadDialog && latestAppVersion ? (
        <VersionReloadDialog
          currentVersion={CURRENT_APP_VERSION}
          latestVersion={latestAppVersion}
        />
      ) : null}
    </SystemConfigContext.Provider>
  )
}

export const useSystemConfig = () => {
  const context = useContext(SystemConfigContext)
  if (!context) {
    throw new Error('useSystemConfig must be used within a SystemConfigProvider')
  }
  return context
}
